<template>
    <SkeletonCard class="mt-4" v-if="isFetching" card-style="long" :num-of-cards=1 />

    <form v-else>
        <ErrorSummary :response-status="responseStatus"/>
        <div class="mt-6">
            <div class="divide-y divide-gray-200">
                <div class="grid grid-cols-1 gap-6 mt-6 md:grid-cols-3">
                    <div class="col-span-1 sm:col-span-1">
                        <label class="block text-sm font-medium text-gray-700" for="gender">Gender
                            <SparklesIcon class="inline w-4 h-4 mx-1 text-powder-600"></SparklesIcon>
                        </label>
                        <SaCombobox
                                id="gender"
                                v-model="profile.gender"
                                :options="profile.genderOptions"
                                name="gender"/>
                    </div>

                    <div class="col-span-1 sm:col-span-1">
                        <label class="block text-sm font-medium text-gray-700" for="heightFeet">Height (feet)

                        </label>
                        <SaCombobox
                                id="heightFeet"
                                v-model="profile.heightFeet"
                                :options="heightFeetOptions"
                                name="heightFeet"/>
                    </div>

                    <div class="col-span-1 sm:col-span-1">
                        <label class="block text-sm font-medium text-gray-700" for="heightInches">Height (inches)
                        </label>
                        <SaCombobox
                                id="heightInches"
                                v-model="profile.heightInches"
                                :options="heightInchesOptions"
                                name="heightInches"/>
                    </div>
                    <div class="col-span-1 sm:col-span-1">
                        <label class="block text-sm font-medium text-gray-700" for="ageRange">Playing Age
                            <SparklesIcon class="inline w-4 h-4 mx-1 text-powder-600"></SparklesIcon>
                        </label>
                        <SaCombobox
                                id="ageRange"
                                v-model="profile.ageRange"
                                :options="profile.ageRangeOptions"
                                name="ageRange"
                                class="capitalize"/>
                        
                        
                        
                    </div>
                    
                    <div class="col-span-1 sm:col-span-1">
                        <label class="block text-sm font-medium text-gray-700" for="bodyType">Body Type</label>
                        <SaCombobox
                                id="bodyType"
                                v-model="profile.bodyType"
                                :options="profile.bodyTypeOptions"
                                name="bodyType"/>
                    </div>
                    <div class="col-span-1 sm:col-span-1">
                        <label class="block text-sm font-medium text-gray-700" for="eyes">Eyes</label>
                        <SaCombobox
                                id="eyes"
                                v-model="profile.eyes"
                                :options="profile.eyesOptions"
                                name="eyes"/>
                    </div>
                    <div class="col-span-1 sm:col-span-1">
                        <label class="block text-sm font-medium text-gray-700" for="hair">Hair</label>
                        <SaCombobox
                                id="hair"
                                v-model="profile.hair"
                                :options="profile.hairOptions"
                                name="hair"/>
                    </div>

                </div>
            </div>
        </div>
        <div class="mt-6">
            <div class="px-6 pb-6 bg-clay-50 divide-y divide-clay-200">
                <h2 class="max-w-2xl pt-6 font-bold text-clay-900">Ethnicity (check all that apply)
                    <SparklesIcon class="inline w-4 h-4 mx-1 text-powder-600"></SparklesIcon>
                </h2>
                <div class="mt-6">
                    <div class="">
                            <fieldset class="space-y-5">
                                <legend class="sr-only">Ethnicity select</legend>
                                <div class="grid gap-1 sm:grid-cols-2 md:grid-cols-3">
                                    <div v-for="option in profile.ethnicityOptions"
                                         class="relative flex items-start">
                                        <div class="flex items-center h-5">
                                            <input :id="option"
                                                   v-model="profile.ethnicity"
                                                   :name="option"
                                                   :value="option"
                                                   aria-describedby=""
                                                   class="w-4 h-4 border-clay-500 rounded text-powder-600 focus:ring-powder-500"
                                                   type="checkbox">
                                        </div>
                                        <div class="ml-3 text-sm">
                                            <label :for="option" class="font-medium text-gray-700">{{ option }}</label>
                                            <span class="text-clay-800"></span>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                    </div>
                </div>
            </div>
        </div>
        <div class="py-3 mt-10 text-center">
            <SaButton @click.prevent="submit" :loading="isSubmitting" variant="primary">
                <template #loading>
                    <span>Processing...</span>
                </template>
            </SaButton>
        </div>


    </form>
    <NotificationSuccess :show="updateSuccessful" content="Your changes were successfully saved."
                         title="Successfully saved!"/>

</template>

<script lang="ts" setup>
// @ts-ignore
import {computed, defineComponent, onMounted, Ref, ref} from "vue";
import {client} from '@/shared';
import {GetProfileAppearance, GetProfileAppearanceResponse, UpdateProfileAppearance} from "@/dtos";
// @ts-ignore
import {SparklesIcon, TrashIcon} from '@heroicons/vue/20/solid';
import {ResponseStatus} from "@servicestack/client";
import ErrorSummary from "@/main/components/ErrorSummary.vue";
import SaCombobox from "@/main/components/SaCombobox.vue";
import NotificationSuccess from "@/main/components/NotificationSuccess.vue";
import SkeletonCard from "@/main/components/skeletons/SkeletonCard.vue";
import SaButton from "@/main/components/SaButton.vue";

const heightFeetOptions = ref([
    '3', '4', '5', '6', '7'
]);

const heightInchesOptions = ref([
    '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'
]);

const responseStatus = ref(null);
const profile: Ref<GetProfileAppearanceResponse> = ref(new GetProfileAppearanceResponse());

const getProfileAppearance = async () => {
    await client.get<GetProfileAppearanceResponse>(new GetProfileAppearance())
        .then(response => {
            profile.value = response;
            profile.value.heightFeet = profile.value.heightFeet === '0' ? '' : profile.value.heightFeet;
            profile.value.heightInches = profile.value.heightInches === '0' ? '' : profile.value.heightInches;
            isFetching.value = false;
        });
}

const isFetching = ref(true);
onMounted(async () => {
    await getProfileAppearance();
});

const isSubmitting = ref(false);
const updateSuccessful: Ref<Boolean> = ref(false);
const submit = async () => {
    isSubmitting.value = true;
    let request = profile.value;
    await client.post<ResponseStatus>(new UpdateProfileAppearance(request))
        .then(_ => {
            updateSuccessful.value = true;
            responseStatus.value = null;
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
    isSubmitting.value = false;
};

</script>
<style>

</style>